import { useEffect, useState } from "react";
import { CaretDown } from "@phosphor-icons/react";
import { ARITH_RE, evalArithmetic } from "./helpers";

export function Section({ tag, title, children }) {
    return (
        <div className="rom-card">
            <div className="rom-card-head">
                <h3><span className="rom-section-tag">{tag}</span> {title}</h3>
            </div>
            <div className="rom-card-body">{children}</div>
        </div>
    );
}

export function CollapsibleSection({ tag, title, summary, defaultOpen = false, open, onOpenChange, children }) {
    const [internalOpen, setInternalOpen] = useState(defaultOpen);
    const isControlled = open !== undefined;
    const isOpen = isControlled ? open : internalOpen;
    const toggle = () => {
        const next = !isOpen;
        if (isControlled) onOpenChange?.(next);
        else setInternalOpen(next);
    };
    return (
        <div className="rom-card">
            <button
                type="button"
                onClick={toggle}
                className="rom-card-head w-full text-left cursor-pointer hover:bg-slate-50/70 transition-colors"
                data-testid={`section-toggle-${tag}`}
                aria-expanded={isOpen}
            >
                <h3><span className="rom-section-tag">{tag}</span> {title}</h3>
                <div className="flex items-center gap-3 flex-wrap">
                    {summary != null && (
                        <span className="font-mono font-bold text-sm text-[var(--rom-orange-dark)] tabular" data-testid={`section-summary-${tag}`}>
                            {summary}
                        </span>
                    )}
                    <CaretDown size={14} weight="bold" className={`text-[var(--rom-text-2)] transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                </div>
            </button>
            {isOpen && <div className="rom-card-body">{children}</div>}
        </div>
    );
}

export function Field({ label, hint, required, children }) {
    return (
        <div>
            <label className="label flex items-center gap-1">
                {label}
                {required && <span className="text-[var(--rom-danger)] font-bold" title="Required for ROM" aria-label="required">*</span>}
            </label>
            {children}
            {hint && <div className="text-[11px] text-[var(--rom-text-2)] mt-1.5">{hint}</div>}
        </div>
    );
}

export function EditableNum({ value, placeholder, onChange, testId }) {
    const [text, setText] = useState(value === undefined || value === null ? "" : String(value));
    const [focused, setFocused] = useState(false);
    useEffect(() => {
        if (!focused) setText(value === undefined || value === null ? "" : String(value));
    }, [value, focused]);

    const isFormula = ARITH_RE.test(text);
    const preview = isFormula ? evalArithmetic(text) : null;

    function handleChange(v) {
        setText(v);
        if (!ARITH_RE.test(v)) onChange(v); // live update for plain numbers
    }
    function commit() {
        if (ARITH_RE.test(text)) {
            const r = evalArithmetic(text);
            if (r.ok) {
                setText(r.value === "" ? "" : String(r.value));
                onChange(r.value);
            }
        }
        setFocused(false);
    }
    return (
        <input
            type="text" inputMode="decimal"
            className={`w-full max-w-[110px] bg-transparent text-right font-mono text-sm border focus:bg-white focus:outline-none rounded px-1.5 py-0.5 ${isFormula ? "border-[var(--rom-orange)]" : "border-transparent hover:border-[var(--rom-border-strong)] focus:border-[var(--rom-orange)]"}`}
            value={text}
            placeholder={placeholder ?? ""}
            title={preview && preview.ok && preview.value !== "" ? `= ${preview.value}` : (preview && !preview.ok ? preview.err : undefined)}
            onFocus={() => setFocused(true)}
            onChange={(e) => handleChange(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); } }}
            data-testid={testId}
        />
    );
}

/* Excel-style formula input. Accepts numbers + arithmetic expressions like
 *   (5+6)+(5*2)  or  =50*30+20*15
 * Evaluates safely on Enter / blur. Only digits, + - * / ( ) . and spaces are allowed. */

export function FormulaInput({ value, placeholder, onCommit, testId }) {
    const [text, setText] = useState(value === "" || value === null || value === undefined ? "" : String(value));
    const [err, setErr] = useState("");
    const [preview, setPreview] = useState(null);

    useEffect(() => {
        // Sync external changes (e.g. job load) into local text
        setText(value === "" || value === null || value === undefined ? "" : String(value));
        setErr("");
        setPreview(null);
    }, [value]);

    function evaluate(raw) {
        const s = raw.trim().replace(/^=/, "");
        if (s === "") return { ok: true, value: "" };
        if (!/^[\d+\-*/().\s]+$/.test(s)) return { ok: false, err: "Only digits and + − × ÷ ( ) allowed" };
        try {
            // eslint-disable-next-line no-new-func
            const out = Function(`"use strict"; return (${s});`)();
            if (typeof out !== "number" || !isFinite(out)) return { ok: false, err: "Invalid formula" };
            return { ok: true, value: Math.max(0, Math.round(out * 100) / 100) };
        } catch {
            return { ok: false, err: "Invalid formula" };
        }
    }

    const isFormula = /[+\-*/()=]/.test(text);

    function onChange(v) {
        setText(v);
        if (/[+\-*/()=]/.test(v)) {
            const r = evaluate(v);
            if (r.ok && r.value !== "") {
                setPreview(r.value);
                setErr("");
            } else if (!r.ok) {
                setPreview(null);
                setErr("");
            } else {
                setPreview(null);
                setErr("");
            }
        } else {
            setPreview(null);
            setErr("");
        }
    }

    function commit() {
        const r = evaluate(text);
        if (!r.ok) {
            setErr(r.err);
            return;
        }
        setErr("");
        setPreview(null);
        if (r.value === "") {
            setText("");
            onCommit("");
            return;
        }
        setText(String(r.value));
        onCommit(r.value);
    }

    return (
        <>
            <input
                type="text"
                inputMode="text"
                className="input font-mono"
                placeholder={placeholder}
                value={text}
                onChange={(e) => onChange(e.target.value)}
                onBlur={commit}
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        e.preventDefault();
                        commit();
                    }
                }}
                data-testid={testId}
            />
            {err && (
                <div className="text-[11px] text-[var(--rom-danger)] mt-1" data-testid={`${testId}-error`}>{err}</div>
            )}
            {!err && isFormula && preview !== null && (
                <div className="text-[11px] text-[var(--rom-text-2)] mt-1 font-mono" data-testid={`${testId}-preview`}>
                    = <b>{Number(preview).toLocaleString()}</b> · press Enter or Tab to apply
                </div>
            )}
        </>
    );
}

export function PillGroup({ value, onChange, options, testId }) {
    return (
        <div className="flex flex-wrap gap-1.5" data-testid={testId}>
            {options.map((o) => (
                <button
                    key={String(o.v)}
                    type="button"
                    onClick={() => onChange(o.v)}
                    data-testid={`${testId}-${String(o.v)}`}
                    className={`px-3 py-2 text-xs font-semibold rounded-[4px] border transition-colors ${
                        value === o.v
                            ? "bg-[var(--rom-navy)] text-white border-[var(--rom-navy)]"
                            : "bg-white text-[var(--rom-text)] border-[var(--rom-border-strong)] hover:border-[var(--rom-navy)]"
                    }`}
                >
                    {o.label}
                </button>
            ))}
        </div>
    );
}

export function Toggle({ label, desc, value, onChange, testId }) {
    return (
        <button
            type="button"
            onClick={() => onChange(!value)}
            data-testid={testId}
            className={`text-left p-3 rounded-[4px] border transition-colors flex items-start justify-between gap-3 ${
                value ? "border-[var(--rom-orange)] bg-[var(--rom-orange-soft)]" : "border-[var(--rom-border-strong)] bg-white"
            }`}
        >
            <div>
                <div className="font-semibold text-sm">{label}</div>
                <div className="text-[11px] text-[var(--rom-text-2)] mt-0.5">{desc}</div>
            </div>
            <div className={`shrink-0 w-9 h-5 rounded-full transition-colors ${value ? "bg-[var(--rom-orange)]" : "bg-slate-300"}`}>
                <div className={`w-4 h-4 bg-white rounded-full mt-0.5 transition-transform ${value ? "translate-x-[18px]" : "translate-x-0.5"}`} />
            </div>
        </button>
    );
}

export function Row({ k, v }) {
    return (
        <div className="flex justify-between">
            <span>{k}</span>
            <span className="font-mono tabular">{v}</span>
        </div>
    );
}

export function SectionToolbar({ dirty, onReset, children }) {
    return (
        <div className="flex items-center justify-between px-4 pt-3 pb-2 -mt-4 -mx-4 border-b border-[var(--rom-border)] gap-3">
            <span className="text-xs text-[var(--rom-text-2)]">{children}</span>
            {dirty && (
                <button onClick={onReset} className="btn btn-ghost btn-sm shrink-0" data-testid="section-reset">
                    Reset to engine defaults
                </button>
            )}
        </div>
    );
}

export function MiscRow({ label, engineVal, ovKey, miscOv, setMiscOv, hint, assumption }) {
    const overridden = miscOv[ovKey] !== undefined;
    return (
        <tr data-testid={`misc-row-${ovKey}`}>
            <td className="font-medium card-title">
                {label}
                {assumption && !overridden && (
                    <span className="ml-2 chip" style={{ padding: "1px 5px", background: "#fef3c7", color: "#92400e" }} title="Assumption · unvalidated" data-testid={`misc-assumption-${ovKey}`}>
                        assumption
                    </span>
                )}
                <div className="text-[10px] text-[var(--rom-text-3)] uppercase tracking-wider">{hint}</div>
            </td>
            <td className="numeric" data-label="Value">
                <div className="flex items-center justify-end gap-2">
                    {overridden && <span className="chip chip-orange" style={{ padding: "1px 5px" }}>edited</span>}
                    <EditableNum
                        step="0.01"
                        value={miscOv[ovKey] ?? engineVal}
                        placeholder={String(engineVal)}
                        onChange={(v) => setMiscOv(ovKey, v)}
                        testId={`misc-${ovKey}`}
                    />
                </div>
            </td>
            <td></td>
        </tr>
    );
}

export function KV({ k, v, bold }) {
    return (
        <div className={`flex justify-between p-3 rounded-[4px] ${bold ? "bg-[var(--rom-orange-soft)]" : "bg-slate-50"}`}>
            <span className="text-[var(--rom-text-2)]">{k}</span>
            <span className={`font-mono tabular ${bold ? "font-bold text-[var(--rom-orange-dark)]" : ""}`}>{v}</span>
        </div>
    );
}
